import { Plus, Trash2 } from 'lucide-react'

import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { OfferModel } from '@/types'

import { CampaignProductPicker, CampaignVariationPicker } from '../CampaignProductPicker'

interface Props {
  rules: Partial<OfferModel.BuyXGetYRules>
  onChange: (rules: Partial<OfferModel.BuyXGetYRules>) => void
}

export const BuyXGetYEditor = ({ rules, onChange }: Props) => {
  const buyItems = rules.buy_items ?? []
  const getItems = rules.get_items ?? []

  // Get-item variation pickers need a product to resolve variations from. The backend rule only stores
  // product_variation_id, so product_id is kept on the row in the editor and stripped before saving.

  const updateBuyIds = (ids: string[]) => {
    const existing = buyItems.filter(b => ids.includes(b.product_id))
    const added = ids
      .filter(id => !existing.some(b => b.product_id === id))
      .map(id => ({ product_id: id, quantity: 1 }))
    onChange({ ...rules, buy_items: [...existing, ...added] })
  }

  const updateBuyQty = (product_id: string, quantity: number) =>
    onChange({ ...rules, buy_items: buyItems.map(b => b.product_id === product_id ? { ...b, quantity } : b) })

  const addGetItem = () =>
    onChange({ ...rules, get_items: [...getItems, { product_variation_id: '', quantity: 1, discount_type: 'percentage', discount_value: 100 }] })

  const removeGetItem = (index: number) =>
    onChange({ ...rules, get_items: getItems.filter((_, i) => i !== index) })

  const updateGetItem = (index: number, patch: Partial<OfferModel.BuyXGetYRules['get_items'][0]>) =>
    onChange({ ...rules, get_items: getItems.map((g, i) => i === index ? { ...g, ...patch } : g) })

  return (
    <div className="space-y-4">
      {/* Buy items */}
      <div className="space-y-3">
        <p className="text-sm font-medium text-ink">Products to buy</p>
        <CampaignProductPicker
          label="Select buy products"
          selectedIds={buyItems.map(b => b.product_id)}
          onChange={updateBuyIds}
          multiple
        />
        {buyItems.map(item => (
          <div key={item.product_id} className="flex items-center gap-3">
            <span className="flex-1 truncate text-sm text-text-2">{item.product_id}</span>
            <div className="flex shrink-0 items-center gap-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                min={1}
                value={item.quantity}
                onChange={(e) => updateBuyQty(item.product_id, Number(e.target.value))}
                className="w-20"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Get items */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 border-t border-border-subtle pt-4">
          <p className="text-sm font-medium text-ink">Products customer gets</p>
          <Button variant="secondary" onClick={addGetItem}>
            <Plus /> Add item
          </Button>
        </div>
        {getItems.map((item, i) => (
          <div key={i} className="space-y-3 rounded-lg border border-border-subtle p-3">
            <div className="space-y-1.5">
              <Label>Product</Label>
              <CampaignProductPicker
                selectedIds={item.product_id ? [item.product_id] : []}
                onChange={(ids) => updateGetItem(i, { product_id: ids[0] ?? '', product_variation_id: '' })}
                multiple={false}
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Variation</Label>
                <CampaignVariationPicker
                  productId={item.product_id ?? ''}
                  value={item.product_variation_id}
                  onChange={(variationId) => updateGetItem(i, { product_variation_id: variationId })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min={1}
                  value={item.quantity}
                  onChange={(e) => updateGetItem(i, { quantity: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Discount type</Label>
                <Select
                  value={item.discount_type}
                  onValueChange={(v) => updateGetItem(i, { discount_type: v as OfferModel.DiscountType })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="cash">Fixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Discount value</Label>
                <Input
                  type="number"
                  min={0}
                  value={item.discount_value}
                  onChange={(e) => updateGetItem(i, { discount_value: Number(e.target.value) })}
                />
              </div>
            </div>
            <Button
              variant="ghost"
             
              onClick={() => removeGetItem(i)}
              className="text-error hover:text-error"
            >
              <Trash2 /> Remove
            </Button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Maximum applications per order</Label>
          <Input
            type="number"
            min={1}
            value={rules.max_applications_per_order ?? 1}
            onChange={(e) => onChange({ ...rules, max_applications_per_order: Number(e.target.value) })}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          checked={rules.free_delivery ?? false}
          onCheckedChange={(v) => onChange({ ...rules, free_delivery: v })}
        />
        <Label>Free delivery</Label>
      </div>
    </div>
  )
}
